/**
 * Socket Manager View
 * Real-time monitoring with software dropdown, email cards, VPS logs
 * Click VPS to open Modal with all events
 */
import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import {
    Row, Col, Card, CardBody, CardHeader,
    Label, Progress, Badge, Input, Button,
    Modal, ModalHeader, ModalBody, Collapse
} from 'reactstrap';


const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const ADMIN_EVENTS = {
    REQUEST_STATS: 'admin:request:stats',
    STATS_UPDATE: 'admin:stats:update',
    REQUEST_VPS_LOGS: 'admin:request:vps:logs',
    VPS_LOGS_UPDATE: 'admin:vps:logs:update',
    CLEAR_VPS_LOGS: 'admin:clear:vps:logs',
    SUBSCRIBE_LOGS: 'admin:subscribe:logs',
    UNSUBSCRIBE_LOGS: 'admin:unsubscribe:logs',
    NEW_LOG: 'admin:new:log'
};

const SocketManager = () => {
    const [connected, setConnected] = useState(false);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [selectedSoftware, setSelectedSoftware] = useState('');

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedVps, setSelectedVps] = useState(null);
    const [vpsLogs, setVpsLogs] = useState([]);
    const [expandedLogs, setExpandedLogs] = useState({});

    const socketRef = useRef(null);

    useEffect(() => {
        try {
            socketRef.current = io(`${SOCKET_URL}/admin`, {
                withCredentials: true,
                transports: ['websocket', 'polling']
            });

            socketRef.current.on('connect', () => {
                setConnected(true);
                setError(null);
            });

            socketRef.current.on(ADMIN_EVENTS.STATS_UPDATE, (data) => {
                setStats(data);
                setLastUpdate(new Date());
                if (!selectedSoftware && data.softwareList?.length > 0) {
                    const firstWithData = data.softwareList.find(sw =>
                        data.dataBySoftware?.[sw.id]?.totalVps > 0
                    );
                    if (firstWithData) {
                        setSelectedSoftware(firstWithData.id);
                    } else {
                        setSelectedSoftware(data.softwareList[0].id);
                    }
                }
            });

            socketRef.current.on(ADMIN_EVENTS.VPS_LOGS_UPDATE, (data) => {
                setVpsLogs(data.logs || []);
            });

            // Listen for new logs in real-time
            socketRef.current.on(ADMIN_EVENTS.NEW_LOG, (data) => {
                setVpsLogs(prev => [...prev, data.log].slice(-50)); // Keep max 50 logs
            });

            socketRef.current.on('connect_error', (err) => {
                setConnected(false);
                setError(err.message);
            });

            socketRef.current.on('disconnect', () => setConnected(false));

        } catch (err) {
            setError('Auth error');
        }

        return () => socketRef.current?.disconnect();
    }, []);

    // Open modal and request logs
    const openVpsModal = (vps) => {
        setSelectedVps(vps);
        setModalOpen(true);
        setExpandedLogs({});
        if (socketRef.current) {
            socketRef.current.emit(ADMIN_EVENTS.REQUEST_VPS_LOGS, { key: vps.username });
            // Subscribe for real-time updates
            socketRef.current.emit(ADMIN_EVENTS.SUBSCRIBE_LOGS, { key: vps.username });
        }
    };

    // Close modal and unsubscribe
    const closeVpsModal = () => {
        if (socketRef.current && selectedVps) {
            socketRef.current.emit(ADMIN_EVENTS.UNSUBSCRIBE_LOGS, { key: selectedVps.username });
        }
        setModalOpen(false);
    };

    // Toggle log expansion
    const toggleLogExpand = (idx) => {
        setExpandedLogs(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    // Format bytes
    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };

    // Format uptime
    const formatUptime = (seconds) => {
        if (!seconds) return '0s';
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${d > 0 ? d + 'd ' : ''}${h > 0 ? h + 'h ' : ''}${m}m`;
    };

    // Format JSON for readability
    const formatData = (data) => {
        if (!data) return 'No data';
        try {
            const parsed = typeof data === 'string' ? JSON.parse(data) : data;
            return JSON.stringify(parsed, null, 2);
        } catch {
            return data;
        }
    };

    // Extract requestId from log data
    const extractRequestId = (log) => {
        // First try direct requestId property (new backend format)
        if (log.requestId) return log.requestId;

        // Fallback: extract from data (old format compatibility)
        if (!log.data) return null;
        try {
            const parsed = typeof log.data === 'string' ? JSON.parse(log.data) : log.data;
            return parsed?.requestId || null;
        } catch {
            return null;
        }
    };

    // Map request events to their expected response events
    const REQUEST_RESPONSE_MAP = {
        'dhbstream:screen:get-screens': 'dhbstream:screen:screens-list',
        'dhbstream:screen:capture-screens': 'dhbstream:screen:capture-result',
        'dhbstream:screen:get-windows': 'dhbstream:screen:windows-list',
        'dhbstream:audio:get-devices': 'dhbstream:audio:devices-list',
        'dhbstream:audio:get-apps': 'dhbstream:audio:apps-list',
        'dhbstream:render-config:get-list': 'dhbstream:render-config:list-response',
        'dhbstream:file-browser:get-drives': 'dhbstream:file-browser:drives-response',
        'dhbstream:file-browser:get-folder': 'dhbstream:file-browser:folder-response',
        'dhbstream:file-browser:get-special-folder': 'dhbstream:file-browser:special-folder-response',
        // Stream edit/log events (web:stream prefix)
        'dhbstream:web:stream:get': 'dhbstream:stream:data',
        'dhbstream:web:stream:log:get': 'dhbstream:stream:log:data',
        // Also map the VPS side stream:get (in case VPS sends different event)
        'dhbstream:stream:get': 'dhbstream:stream:data',
        'dhbstream:stream:log:get': 'dhbstream:stream:log:data',
    };

    // Build reverse map (response -> request)
    const RESPONSE_REQUEST_MAP = Object.fromEntries(
        Object.entries(REQUEST_RESPONSE_MAP).map(([req, res]) => [res, req])
    );

    // Group logs by matching request/response
    // Strategy: 
    // 1. If both have requestId - match by requestId
    // 2. If request has no requestId but response has - match by event name pattern + timing
    const groupLogsByRequestId = (logs) => {
        const grouped = [];
        const processedIndices = new Set();

        // Sort logs by timestamp (oldest first for processing)
        const sortedLogs = [...logs].sort((a, b) =>
            new Date(a.timestamp) - new Date(b.timestamp)
        );

        sortedLogs.forEach((log, idx) => {
            if (processedIndices.has(idx)) return;

            // Skip connection logs - don't group them
            if (log.type === 'connection') {
                grouped.push({ type: 'connection', log });
                processedIndices.add(idx);
                return;
            }

            const requestId = extractRequestId(log);

            // Check if this is a request (web-request)
            if (log.socketType === 'web-request') {
                const expectedResponseEvent = REQUEST_RESPONSE_MAP[log.eventName];

                if (expectedResponseEvent) {
                    // Look for matching response
                    let matchedResponseIdx = -1;

                    // First try to match by requestId (if present)
                    if (requestId) {
                        for (let j = idx + 1; j < sortedLogs.length; j++) {
                            if (processedIndices.has(j)) continue;
                            const potentialResponse = sortedLogs[j];
                            const responseRequestId = extractRequestId(potentialResponse);
                            if (responseRequestId === requestId &&
                                potentialResponse.eventName === expectedResponseEvent) {
                                matchedResponseIdx = j;
                                break;
                            }
                        }
                    }

                    // If no match by requestId, try by event name pattern (within 5 second window)
                    if (matchedResponseIdx === -1) {
                        const requestTime = new Date(log.timestamp).getTime();
                        for (let j = idx + 1; j < sortedLogs.length; j++) {
                            if (processedIndices.has(j)) continue;
                            const potentialResponse = sortedLogs[j];
                            const responseTime = new Date(potentialResponse.timestamp).getTime();

                            // Only match within 5 second window
                            if (responseTime - requestTime > 5000) break;

                            if (potentialResponse.eventName === expectedResponseEvent &&
                                potentialResponse.socketType !== 'web-request') {
                                matchedResponseIdx = j;
                                break;
                            }
                        }
                    }

                    if (matchedResponseIdx !== -1) {
                        const responseLog = sortedLogs[matchedResponseIdx];
                        grouped.push({
                            type: 'paired',
                            requestId: requestId || extractRequestId(responseLog) || 'event-match',
                            request: log,
                            response: responseLog,
                            timestamp: log.timestamp,
                            eventName: log.eventName
                        });
                        processedIndices.add(idx);
                        processedIndices.add(matchedResponseIdx);
                        return;
                    }
                }

                // No match found - standalone request (pending)
                grouped.push({
                    type: 'paired',
                    requestId: requestId || 'pending',
                    request: log,
                    response: null,
                    timestamp: log.timestamp,
                    eventName: log.eventName
                });
                processedIndices.add(idx);
                return;
            }

            // This is a response without a matching request
            // Check if it matches any known response pattern
            const expectedRequestEvent = RESPONSE_REQUEST_MAP[log.eventName];
            if (expectedRequestEvent) {
                // Look backwards for unprocessed request
                for (let j = idx - 1; j >= 0; j--) {
                    if (processedIndices.has(j)) continue;
                    const potentialRequest = sortedLogs[j];
                    if (potentialRequest.eventName === expectedRequestEvent &&
                        potentialRequest.socketType === 'web-request') {
                        const requestTime = new Date(potentialRequest.timestamp).getTime();
                        const responseTime = new Date(log.timestamp).getTime();
                        // Only match within 5 second window
                        if (responseTime - requestTime <= 5000) {
                            grouped.push({
                                type: 'paired',
                                requestId: requestId || extractRequestId(potentialRequest) || 'event-match',
                                request: potentialRequest,
                                response: log,
                                timestamp: potentialRequest.timestamp,
                                eventName: potentialRequest.eventName
                            });
                            processedIndices.add(idx);
                            processedIndices.add(j);
                            return;
                        }
                    }
                }
            }

            // Standalone response (no matching request)
            grouped.push({ type: 'standalone', log });
            processedIndices.add(idx);
        });

        // Sort by timestamp descending (newest first)
        grouped.sort((a, b) => {
            const timeA = a.timestamp || a.log?.timestamp;
            const timeB = b.timestamp || b.log?.timestamp;
            return new Date(timeB) - new Date(timeA);
        });

        return grouped;
    };

    // Shared card styles
    const cardStyle = {
        borderRadius: '20px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: 'none',
        overflow: 'hidden',
        marginBottom: '20px'
    };

    const headerStyle = (gradient) => ({
        background: gradient,
        padding: '20px 24px',
        borderBottom: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    });

    const headerLeft = (icon, title, subtitle) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <i className={`fas ${icon}`} style={{ fontSize: '32px', color: '#fff', opacity: 0.9 }}></i>
            <div style={{ lineHeight: '1.3' }}>
                <Label style={{ fontSize: '16px', color: '#fff', fontWeight: '700', marginBottom: '0', display: 'block' }}>
                    {title}
                </Label>
                {subtitle && (
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>
                        {subtitle}
                    </span>
                )}
            </div>
        </div>
    );

    // Render connection status
    const renderStatus = () => (
        <Card style={cardStyle}>
            <CardHeader style={headerStyle(connected
                ? 'linear-gradient(135deg, #198754 0%, #0f5132 100%)'
                : 'linear-gradient(135deg, #dc3545 0%, #842029 100%)')}>
                {headerLeft(
                    connected ? 'fa-check-circle' : 'fa-times-circle',
                    connected ? 'Connected' : 'Disconnected',
                    lastUpdate ? `Updated: ${lastUpdate.toLocaleTimeString()}` : null
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {error && (
                        <Badge style={{
                            background: 'rgba(255,255,255,0.2)',
                            color: '#fff',
                            fontSize: '12px',
                            padding: '6px 14px',
                            borderRadius: '20px'
                        }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '4px' }}></i> {error}
                        </Badge>
                    )}
                    <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background: connected ? '#4ade80' : '#f87171',
                        boxShadow: connected ? '0 0 8px rgba(74,222,128,0.6)' : '0 0 8px rgba(248,113,113,0.6)',
                        animation: connected ? 'pulse 2s infinite' : 'none'
                    }} />
                </div>
            </CardHeader>
        </Card>
    );

    // Render system stats
    const renderSystem = () => {
        if (!stats?.system) return null;
        const sys = stats.system;
        const totalMemGB = (sys.totalMemory / (1024 * 1024 * 1024)).toFixed(1);
        const usedMemGB = (sys.usedMemory / (1024 * 1024 * 1024)).toFixed(1);
        const freeMemGB = (sys.freeMemory / (1024 * 1024 * 1024)).toFixed(1);
        return (
            <Card style={cardStyle}>
                <CardHeader style={headerStyle('linear-gradient(135deg, #3498db 0%, #2c3e50 100%)')}>
                    {headerLeft('fa-desktop', sys.hostname, `${sys.platform} • Uptime: ${formatUptime(sys.uptime)}`)}
                </CardHeader>
                <CardBody style={{ padding: '20px 24px' }}>
                    <Row>
                        <Col md="6">
                            <div style={{
                                padding: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '20px',
                                marginBottom: '12px'
                            }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fas fa-th-large" style={{ fontSize: '20px' }}></i>
                                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>CPU</span>
                                    </div>
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        color: sys.cpuUsageAvg > 80 ? '#dc3545' : '#2c3e50',
                                        background: sys.cpuUsageAvg > 80 ? 'rgba(220,53,69,0.1)' : 'rgba(23,162,184,0.1)',
                                        padding: '3px 10px',
                                        borderRadius: '20px'
                                    }}>
                                        {sys.cpuUsageAvg}%
                                    </span>
                                </div>
                                <Progress value={sys.cpuUsageAvg} color={sys.cpuUsageAvg > 80 ? 'danger' : 'info'} style={{ height: '6px', borderRadius: '3px', marginBottom: '8px' }} />
                                <div style={{ fontSize: '12px', color: '#8898aa' }}>
                                    {sys.cpuCount} cores
                                </div>
                            </div>
                        </Col>
                        <Col md="6">
                            <div style={{
                                padding: '16px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '20px',
                                marginBottom: '12px'
                            }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <i className="fas fa-save" style={{ fontSize: '20px' }}></i>
                                        <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}>RAM</span>
                                    </div>
                                    <span style={{
                                        fontSize: '14px',
                                        fontWeight: '700',
                                        color: sys.memoryUsagePercent > 80 ? '#dc3545' : '#2c3e50',
                                        background: sys.memoryUsagePercent > 80 ? 'rgba(220,53,69,0.1)' : 'rgba(40,167,69,0.1)',
                                        padding: '3px 10px',
                                        borderRadius: '20px'
                                    }}>
                                        {sys.memoryUsagePercent}%
                                    </span>
                                </div>
                                <Progress value={sys.memoryUsagePercent} color={sys.memoryUsagePercent > 80 ? 'danger' : 'success'} style={{ height: '6px', borderRadius: '3px', marginBottom: '8px' }} />
                                <div style={{ fontSize: '12px', color: '#8898aa' }}>
                                    {usedMemGB} GB / {totalMemGB} GB — Trống: {freeMemGB} GB
                                </div>
                            </div>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        );
    };

    // Render software dropdown
    const renderSoftwareDropdown = () => {
        if (!stats?.softwareList) return null;
        return (
            <Card style={cardStyle}>
                <CardHeader style={headerStyle('linear-gradient(135deg, #a044ff 0%, #6a3093 100%)')}>
                    {headerLeft('fa-cube', 'Software', `${stats.softwareList.length} phần mềm`)}
                </CardHeader>
                <CardBody style={{ padding: '20px 24px' }}>
                    <Row className="align-items-center">
                        <Col md="4">
                            <Input
                                type="select"
                                value={selectedSoftware}
                                onChange={e => setSelectedSoftware(e.target.value)}
                                style={{ fontSize: '14px', padding: '10px 14px', borderRadius: '20px', border: '1px solid #e0e0e0' }}
                            >
                                {stats.softwareList.map(sw => (
                                    <option key={sw.id} value={sw.id}>{sw.name}</option>
                                ))}
                            </Input>
                        </Col>
                        <Col md="8" style={{ display: 'flex', alignItems: 'center', gap: '30px', justifyContent: 'flex-end' }}>
                            {selectedSoftware && stats.dataBySoftware?.[selectedSoftware] && (
                                <>
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '8px 24px',
                                        backgroundColor: 'rgba(81, 203, 206, 0.1)',
                                        borderRadius: '20px',
                                        border: '1px solid rgba(81, 203, 206, 0.2)'
                                    }}>
                                        <div style={{ fontSize: '28px', fontWeight: '800', color: '#51cbce' }}>
                                            {stats.dataBySoftware[selectedSoftware].totalVps || 0}
                                        </div>
                                        <span style={{ color: '#666', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>VPS</span>
                                    </div>
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '8px 24px',
                                        backgroundColor: 'rgba(107, 208, 152, 0.1)',
                                        borderRadius: '20px',
                                        border: '1px solid rgba(107, 208, 152, 0.2)'
                                    }}>
                                        <div style={{ fontSize: '28px', fontWeight: '800', color: '#6bd098' }}>
                                            {Object.keys(stats.dataBySoftware[selectedSoftware].emails || {}).length}
                                        </div>
                                        <span style={{ color: '#666', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Emails</span>
                                    </div>
                                </>
                            )}
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        );
    };

    // Render email cards
    const renderEmailCards = () => {
        if (!selectedSoftware || !stats?.dataBySoftware?.[selectedSoftware]) return null;
        const softwareData = stats.dataBySoftware[selectedSoftware];
        const emails = Object.entries(softwareData.emails || {});

        if (emails.length === 0) {
            return (
                <Card style={{ ...cardStyle, padding: '40px', textAlign: 'center', color: '#adb5bd' }}>
                    <i className="fas fa-cloud-download-alt" style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.4 }}></i>
                    <div style={{ fontSize: '16px' }}>No connections</div>
                </Card>
            );
        }

        return (
            <Row>
                {emails.map(([email, emailData]) => (
                    <Col md="6" lg="4" key={email} style={{ marginBottom: '20px' }}>
                        <Card style={{ ...cardStyle, height: '100%' }}>
                            <CardHeader style={{
                                padding: '14px 20px',
                                background: 'linear-gradient(135deg, #e9ecef 0%, #f8f9fa 100%)',
                                borderBottom: '1px solid #e9ecef',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <Label style={{ fontSize: '14px', fontWeight: '700', margin: 0, color: '#2c3e50' }}>
                                    <i className="fas fa-envelope" style={{ marginRight: '4px' }}></i> {email}
                                </Label>
                                <Badge style={{
                                    background: 'linear-gradient(135deg, #a044ff, #6a3093)',
                                    color: '#fff',
                                    fontSize: '12px',
                                    padding: '4px 12px',
                                    borderRadius: '20px'
                                }}>
                                    {emailData.vpsCount} VPS
                                </Badge>
                            </CardHeader>
                            <CardBody style={{ padding: '12px 16px', maxHeight: '350px', overflowY: 'auto' }}>
                                {emailData.vpsList?.map((vps, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => openVpsModal(vps)}
                                        style={{
                                            padding: '12px 14px',
                                            marginBottom: '10px',
                                            backgroundColor: '#fafbfc',
                                            borderRadius: '20px',
                                            cursor: 'pointer',
                                            border: '1px solid #e9ecef',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseOver={e => {
                                            e.currentTarget.style.backgroundColor = '#f0f4f8';
                                            e.currentTarget.style.borderColor = '#c8d6e5';
                                            e.currentTarget.style.transform = 'translateY(-1px)';
                                        }}
                                        onMouseOut={e => {
                                            e.currentTarget.style.backgroundColor = '#fafbfc';
                                            e.currentTarget.style.borderColor = '#e9ecef';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <span style={{ fontSize: '14px', fontWeight: '600', color: '#2c3e50' }}><i className="fas fa-desktop" style={{ marginRight: '4px' }}></i> {vps.vpsName}</span>
                                                <div style={{ fontSize: '12px', color: '#8898aa', marginTop: '2px' }}>{vps.username}</div>
                                            </div>
                                            <span style={{ fontSize: '14px', color: '#c8d6e5' }}>▶</span>
                                        </div>
                                        {/* Recent logs summary */}
                                        {vps.recentLogs?.length > 0 && (
                                            <div style={{ marginTop: '8px', fontSize: '11px', color: '#8898aa' }}>
                                                {vps.recentLogs.slice(-2).map((log, i) => (
                                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                        <span>{log.action === 'connected' ? <i className="fas fa-circle" style={{ color: '#28a745', fontSize: '10px' }}></i> : <i className="fas fa-circle" style={{ color: '#dc3545', fontSize: '10px' }}></i>}</span>
                                                        <span>{log.socketType} {log.action}</span>
                                                        <span style={{ color: '#c8d6e5', marginLeft: 'auto', fontSize: '10px' }}>
                                                            {new Date(log.timestamp).toLocaleTimeString()}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Web clients */}
                                {emailData.webClients?.map((client, idx) => (
                                    <div key={`web-${idx}`} style={{
                                        padding: '10px 14px',
                                        marginBottom: '8px',
                                        backgroundColor: '#e8f4f8',
                                        borderRadius: '20px',
                                        fontSize: '13px',
                                        color: '#2c3e50'
                                    }}>
                                        <i className="fas fa-globe" style={{ marginRight: '4px' }}></i> Web: {client.accounts?.join(', ') || client.email}
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>
        );
    };

    // Render modal with all logs - 80% height
    const renderModal = () => (
        <Modal
            isOpen={modalOpen}
            toggle={closeVpsModal}
            size="xl"
            style={{ maxWidth: '50%', maxHeight: '90%' }}
        >
            <ModalHeader toggle={closeVpsModal} style={{
                background: 'linear-gradient(135deg, #3498db 0%, #2c3e50 100%)',
                color: '#fff',
                borderRadius: '0',
                padding: '16px 24px'
            }}>
                <span style={{ color: '#fff', fontSize: '16px', fontWeight: '600' }}>
                    <i className="fas fa-desktop" style={{ marginRight: '4px' }}></i> {selectedVps?.vpsName} — {selectedVps?.username}
                </span>
            </ModalHeader>
            <ModalBody style={{ height: '80vh', overflowY: 'auto', padding: '20px' }}>
                <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Button color="info" size="sm" style={{ borderRadius: '20px', fontSize: '12px' }} onClick={() => {
                        if (socketRef.current && selectedVps) {
                            socketRef.current.emit(ADMIN_EVENTS.REQUEST_VPS_LOGS, { key: selectedVps.username });
                        }
                    }}>
                        <i className="fas fa-sync-alt" style={{ marginRight: '4px' }} /> Refresh
                    </Button>
                    <Button color="danger" size="sm" style={{ borderRadius: '20px', fontSize: '12px' }} onClick={() => {
                        if (socketRef.current && selectedVps) {
                            socketRef.current.emit(ADMIN_EVENTS.CLEAR_VPS_LOGS, { key: selectedVps.username });
                            setVpsLogs([]); // Clear local logs immediately
                        }
                    }}>
                        <i className="fas fa-times" style={{ marginRight: '4px' }} /> Clear All
                    </Button>
                    <span style={{ fontSize: '13px', color: '#8898aa', marginLeft: 'auto' }}>{vpsLogs.length} logs (max 50)</span>
                </div>

                {
                    vpsLogs.length === 0 ? (
                        <div style={{ color: '#adb5bd', textAlign: 'center', fontSize: '16px', padding: '40px' }}>
                            <i className="fas fa-file-alt" style={{ fontSize: '48px', display: 'block', marginBottom: '12px', opacity: 0.4 }}></i>
                            No logs available
                        </div>
                    ) : (
                        <div>
                            {groupLogsByRequestId(vpsLogs).map((item, idx) => {
                                // Connection log
                                if (item.type === 'connection') {
                                    const log = item.log;
                                    return (
                                        <div key={idx} style={{ marginBottom: '8px', backgroundColor: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '20px', overflow: 'hidden' }}>
                                            <div onClick={() => toggleLogExpand(idx)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <Badge color={log.action === 'connected' ? 'success' : 'danger'} style={{ fontSize: '11px', borderRadius: '20px' }}>
                                                        {log.action === 'connected' ? <><i className="fas fa-circle" style={{ color: '#28a745', fontSize: '8px', marginRight: '4px' }}></i> CONNECTED</> : <><i className="fas fa-circle" style={{ color: '#dc3545', fontSize: '8px', marginRight: '4px' }}></i> DISCONNECTED</>}
                                                    </Badge>
                                                    <Badge color="secondary" style={{ fontSize: '11px', borderRadius: '20px' }}>{log.socketType}</Badge>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <span style={{ color: '#8898aa', fontSize: '12px' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                                                    <span style={{ fontSize: '12px', color: '#c8d6e5' }}>{expandedLogs[idx] ? '▼' : '▶'}</span>
                                                </div>
                                            </div>
                                            <Collapse isOpen={expandedLogs[idx]}>
                                                <div style={{ padding: '14px 18px', backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '12px', borderTop: '1px solid #e9ecef' }}>
                                                    <div><span style={{ color: '#9cdcfe' }}>Tool:</span> {log.tool}</div>
                                                    <div><span style={{ color: '#9cdcfe' }}>Email:</span> {log.email || 'N/A'}</div>
                                                    <div><span style={{ color: '#9cdcfe' }}>Socket ID:</span> {log.socketId}</div>
                                                    {log.reason && <div><span style={{ color: '#9cdcfe' }}>Reason:</span> {log.reason}</div>}
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                }

                                // Paired request/response log
                                if (item.type === 'paired') {
                                    const reqEvent = item.request?.eventName || '(pending)';
                                    const resEvent = item.response?.eventName || '(pending)';
                                    return (
                                        <div key={idx} style={{ marginBottom: '8px', backgroundColor: '#fff', border: '1px solid rgba(23,162,184,0.3)', borderRadius: '20px', overflow: 'hidden' }}>
                                            <div onClick={() => toggleLogExpand(idx)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <Badge style={{ fontSize: '11px', borderRadius: '6px', background: 'linear-gradient(135deg, #17a2b8, #138496)', color: '#fff' }}><i className="fas fa-globe" style={{ marginRight: '4px' }}></i> Web → Tool</Badge>
                                                    <span style={{ fontSize: '13px', fontFamily: 'Consolas, monospace', fontWeight: 'bold', color: '#2c3e50' }}>{reqEvent}</span>
                                                    {item.response && (
                                                        <>
                                                            <span style={{ color: '#c8d6e5' }}>→</span>
                                                            <span style={{ fontSize: '13px', fontFamily: 'Consolas, monospace', color: '#198754', fontWeight: '600' }}>{resEvent}</span>
                                                        </>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <span style={{ color: '#8898aa', fontSize: '12px' }}>{new Date(item.timestamp).toLocaleTimeString()}</span>
                                                    <span style={{ fontSize: '12px', color: '#c8d6e5' }}>{expandedLogs[idx] ? '▼' : '▶'}</span>
                                                </div>
                                            </div>
                                            <Collapse isOpen={expandedLogs[idx]}>
                                                <div style={{ borderTop: '1px solid #e9ecef' }}>
                                                    {/* Request Section */}
                                                    <div style={{ padding: '12px 18px', backgroundColor: '#e3f2fd' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#1976d2', marginBottom: '8px', fontSize: '13px' }}><i className="fas fa-download" style={{ marginRight: '4px' }}></i> REQUEST: {reqEvent}</div>
                                                        <div style={{ backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '12px', padding: '10px', borderRadius: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflowY: 'auto' }}>
                                                            {item.request ? formatData(item.request.data) : '(No request data)'}
                                                        </div>
                                                    </div>
                                                    {/* Response Section */}
                                                    <div style={{ padding: '12px 18px', backgroundColor: '#e8f5e9' }}>
                                                        <div style={{ fontWeight: 'bold', color: '#388e3c', marginBottom: '8px', fontSize: '13px' }}><i className="fas fa-upload" style={{ marginRight: '4px' }}></i> RESPONSE: {resEvent}</div>
                                                        <div style={{ backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '12px', padding: '10px', borderRadius: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px', overflowY: 'auto' }}>
                                                            {item.response ? formatData(item.response.data) : '(Waiting for response...)'}
                                                        </div>
                                                    </div>
                                                </div>
                                            </Collapse>
                                        </div>
                                    );
                                }

                                // Standalone log (no requestId)
                                const log = item.log;
                                return (
                                    <div key={idx} style={{ marginBottom: '8px', backgroundColor: '#fff', border: '1px solid #e9ecef', borderRadius: '20px', overflow: 'hidden' }}>
                                        <div onClick={() => toggleLogExpand(idx)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Badge color={log.socketType === 'web-request' ? 'info' : 'success'} style={{ fontSize: '11px', borderRadius: '20px' }}>
                                                    {log.socketType === 'web-request' ? <><i className="fas fa-globe" style={{ marginRight: '4px' }}></i> Web → Tool</> : <><i className="fas fa-upload" style={{ marginRight: '4px' }}></i> Tool → Web</>}
                                                </Badge>
                                                <span style={{ fontSize: '13px', fontFamily: 'Consolas, monospace', fontWeight: 'bold', color: '#2c3e50' }}>{log.eventName}</span>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span style={{ color: '#8898aa', fontSize: '12px' }}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                                                <span style={{ fontSize: '12px', color: '#c8d6e5' }}>{expandedLogs[idx] ? '▼' : '▶'}</span>
                                            </div>
                                        </div>
                                        <Collapse isOpen={expandedLogs[idx]}>
                                            <div style={{ padding: '14px 18px', backgroundColor: '#1e1e1e', color: '#e0e0e0', fontFamily: 'Consolas, Monaco, monospace', fontSize: '12px', borderTop: '1px solid #e9ecef', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '300px', overflowY: 'auto' }}>
                                                {formatData(log.data)}
                                            </div>
                                        </Collapse>
                                    </div>
                                );
                            })}
                        </div>
                    )
                }
            </ModalBody >
        </Modal >
    );

    return (
        <div className="content" style={{ fontSize: '15px' }}>
            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
            {renderStatus()}
            {renderSystem()}
            {renderSoftwareDropdown()}
            {renderEmailCards()}
            {renderModal()}
        </div>
    );
};

export default SocketManager;
